import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Cpu, Wifi, WifiOff, AlertOctagon, Activity, FlaskConical, ArrowRight,
} from "lucide-react";
import { useSummary } from "@/hooks/useSummary";
import { useOrgOptics } from "@/hooks/useResults";
import { useOrg } from "@/hooks/useOrg";
import { qpcrApi } from "@/api/qpcr";
import { useQuery } from "@tanstack/react-query";
import {
  PageHeader, StatCard, DataTable, EmptyState, type Column,
} from "@/components/ui";
import type { OrgOptic } from "@/api/results";

// Org home — results-forward: fleet health at a glance + the most recent runs
// across the whole org.
export function Overview() {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const { data: summary } = useSummary();
  const { data: opticsData, isLoading } = useOrgOptics(25);
  const pathogensQ = useQuery({
    queryKey: ["pathogens"],
    queryFn: () => qpcrApi.pathogens(),
    staleTime: 5 * 60_000,
  });

  const s = summary?.summary;
  const pathogenName = useMemo(() => {
    const m = new Map<number, string>();
    (pathogensQ.data?.pathogens ?? []).forEach((p) => m.set(p.id, p.name));
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [pathogensQ.data]);

  const recent = (opticsData?.optics ?? []).filter((o) => !o.hidden).slice(0, 10);

  const columns: Column<OrgOptic>[] = [
    {
      key: "received_at",
      header: "Time",
      render: (o) => new Date(o.received_at).toLocaleString(),
      numeric: true,
    },
    {
      key: "device",
      header: "Device",
      render: (o) => o.device_label ?? o.device_serial ?? "—",
    },
    {
      key: "pathogens",
      header: "Targets",
      render: (o) =>
        o.pathogen_ids.length === 0
          ? <span className="text-textLight">—</span>
          : (
            <span className="flex flex-wrap gap-xs">
              {o.pathogen_ids.map((pid) => (
                <span key={pid} className="inline-flex items-center rounded-sm bg-purple-bg text-purple-text px-sm py-[2px] text-xs">
                  {pathogenName(pid)}
                </span>
              ))}
            </span>
          ),
    },
  ];

  return (
    <div className="space-y-lg">
      <PageHeader
        title="Overview"
        subtitle={currentOrg?.name ? `${currentOrg.name} · fleet at a glance` : undefined}
      />

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-md">
          <StatCard label="Devices" value={s.devices_total} icon={Cpu} tone="brand" />
          <StatCard label="Online" value={s.devices_online} icon={Wifi} tone="success" />
          <StatCard label="Offline" value={s.devices_offline} icon={WifiOff} />
          <StatCard
            label="Errors · 24h"
            value={s.errors_last_24h}
            icon={AlertOctagon}
            tone={s.errors_last_24h > 0 ? "danger" : "default"}
          />
          <StatCard label="Optics · 24h" value={s.optics_last_24h} icon={Activity} />
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-sm">
          <h2 className="text-sm font-semibold text-text">Recent results</h2>
          <button
            type="button"
            onClick={() => navigate("/results")}
            className="flex items-center gap-xs text-sm text-brand hover:text-brand-dark cursor-pointer"
          >
            All results <ArrowRight size={14} />
          </button>
        </div>
        {!isLoading && recent.length === 0 ? (
          <EmptyState title="No results yet" hint="Runs across your fleet will appear here." icon={FlaskConical} />
        ) : (
          <DataTable
            columns={columns}
            rows={recent}
            getRowKey={(o) => o.id}
            loading={isLoading}
            onRowClick={(o) => navigate(`/devices/${o.device_id}/results/${o.id}`)}
          />
        )}
      </div>
    </div>
  );
}

export default Overview;
